/**
 * WHERE the one-time onboarding wizard is allowed to open — the single
 * authoritative answer, shared by every surface.
 *
 * QA: "Onboarding quick setup modal appears during active build and blocks
 * workspace". The wizard is a full-screen takeover: opening it over the builder
 * replaces chat, preview, terminal and the build progress pane all at once. The
 * build itself survives (the page component does not unmount and the SSE reader
 * carries no abort signal), but the user is left staring at a survey with no way
 * back to work they can see running.
 *
 * WHY A PHASE CHECK WAS NOT ENOUGH
 *
 * The previous fix asked "is a build running right now?" (./build-activity-gate)
 * at the moment the builder opened the wizard. That is necessary but not
 * sufficient, because the builder's own entry point is the instant BEFORE the
 * first build starts — every phase signal is idle there by construction. So the
 * wizard still landed on the project workspace, and QA still saw it over a
 * build: the classifier, the ownership step or a queued follow-up would move the
 * page into a live build a beat later, with the survey already on top.
 *
 * The ticket's actual requirement is about place, not timing: "Onboarding
 * questions should only appear at the intended post-signup entry point."
 *
 * THE RULE
 *
 *   The project builder is NEVER an onboarding entry point. Not before the first
 *   build, not between builds, not when everything happens to be idle.
 *
 * Onboarding belongs to the post-signup surface (the home overlay), and only
 * when that surface is not itself on its way into a build — a stashed prompt, a
 * parked clone intent or a `?prompt=` in the URL all mean "this load exists to
 * hand off into the builder", and a survey dropped in front of that is the same
 * interruption wearing a different route.
 *
 * DEFERRING IS NOT DISMISSING
 *
 * A blocked entry point must persist NOTHING — no `onboarding_shown_at`, no
 * local marker. The user is offered the wizard again the next time they land on
 * the home surface with nothing to resume, which is exactly the ticket's "defer
 * it until the user is idle and not inside an active build workspace". Stamping
 * here would opt a user out, permanently and on every device, of a wizard they
 * never saw — see ./verification-gate.ts for that incident.
 */

import {
  onboardingDeferReason,
  type OnboardingBuildSignals,
  type OnboardingDeferReason,
} from "./build-activity-gate";

/**
 * The surfaces that can even ask the question. `builder` is listed so it can be
 * refused by name rather than by omission: a call site that passes it gets a
 * blocked decision and the telemetry that explains why, instead of silently
 * doing nothing.
 */
export type OnboardingEntrySurface = "home" | "builder";

/**
 * Machine-readable cause, reported as `suppressed_reason` on the
 * `onboarding_suppressed` event so the funnel can answer "how often does
 * onboarding lose to a build, and to what?" without a code read.
 *
 * The build-phase reasons are re-exported from ./build-activity-gate rather
 * than restated, so the vocabulary cannot drift between the two modules.
 */
export type OnboardingBlockedReason =
  /** The project builder. Refused unconditionally — see the module note. */
  | "active_builder"
  /** This load exists to hand a prompt / clone off into a build. */
  | "pending_build_handoff"
  | OnboardingDeferReason;

export interface OnboardingEntryContext {
  surface: OnboardingEntrySurface;
  /**
   * A stashed home prompt, a parked clone intent, a `?prompt=` in the URL, or a
   * resume this page has already fired. Any of them means a build is imminent.
   */
  pendingBuildHandoff?: boolean;
  /**
   * Live build / repair / publish phases, when the surface tracks them. The
   * home overlay does not run builds, so it has none to pass; the builder does,
   * and passes them purely so the telemetry names the phase that was running.
   */
  signals?: OnboardingBuildSignals;
}

/**
 * `null` means "open the wizard". Any other value is the reason not to.
 *
 * Order matters only for which reason gets reported, never for the verdict:
 * the surface is checked first so a builder is always reported as
 * `active_builder`, then the handoff, then whichever phase is live.
 */
export function onboardingEntryBlockedReason(
  ctx: OnboardingEntryContext,
): OnboardingBlockedReason | null {
  if (ctx.surface === "builder") return "active_builder";
  if (ctx.pendingBuildHandoff) return "pending_build_handoff";
  return ctx.signals ? onboardingDeferReason(ctx.signals) : null;
}

/** Convenience for call sites that only need the boolean. */
export function canOpenOnboarding(ctx: OnboardingEntryContext): boolean {
  return onboardingEntryBlockedReason(ctx) === null;
}


/**
 * The home surface's four "a build is imminent" signals, as data.
 *
 * A pure function rather than four inline `||`s at the call site, because this
 * is the half of the fix that is easy to get subtly wrong and impossible to
 * unit-test inside a ~4,500-line client component. In particular `resumeFired`
 * is the one the other three miss: the home resume effect CONSUMES the stash on
 * mount, so a stash read taken after an `await` can come back empty while a
 * project is already being created and navigated to.
 */
export interface HomeHandoffSignals {
  /** `?prompt=` — the home prompt survived signup in the query string. */
  urlPrompt: boolean;
  /** It survived signup in storage instead. */
  stashedPrompt: boolean;
  /** A parked "clone this template" intent. */
  cloneIntent: boolean;
  /** This page has already fired a resume; a navigation is under way. */
  resumeFired: boolean;
}

/** Build the entry context for the home surface from its raw signals. */
export function homeOnboardingEntryContext(
  signals: HomeHandoffSignals,
): OnboardingEntryContext {
  return {
    surface: "home",
    pendingBuildHandoff:
      signals.urlPrompt ||
      signals.stashedPrompt ||
      signals.cloneIntent ||
      signals.resumeFired,
  };
}
